/**
 * BasicMetadataSection Component
 *
 * Displays basic metadata information for audit items (topic, language, content type, labels)
 * Extracted from AuditItemContent to improve maintainability and follow component strategy patterns.
 */

import type { GraphQLAuditItem } from '@internal-audit/domain/graphql-types';
import { auditItemHelpers } from '@internal-audit/domain/graphql-types';
import { Badge, BadgeSize } from '@soteria/design-system-ve';

import * as styles from '../AuditItem.css';

interface BasicMetadataSectionProps {
  item: GraphQLAuditItem;
  topic?: string;
  contentType?: string;
}

export function BasicMetadataSection({ item, topic, contentType }: BasicMetadataSectionProps) {
  const language = auditItemHelpers.getContentLanguage(item);
  const displayTopic = topic || 'Unknown';
  const displayContentType = contentType || 'Unknown';

  return (
    <>
      <div className={styles.metadataItem}>
        <span className={styles.label}>Topic:</span>
        <Badge size={BadgeSize.Small} text={displayTopic} />
      </div>
      <div className={styles.metadataItem}>
        <span className={styles.label}>Detected Language:</span>
        <Badge size={BadgeSize.Small} text={language} />
      </div>
      <div className={styles.metadataItem}>
        <span className={styles.label}>Content Type:</span>
        <Badge size={BadgeSize.Small} text={displayContentType} />
      </div>
      <div className={styles.metadataItem}>
        <span className={styles.label}>Labels:</span>
        <div className={styles.labelBadges}>
          {item.contentSnapshot?.classificationResults?.map(result => {
            const displayText = result.name.toLowerCase() === 'safe' ? result.name.toUpperCase() : `${result.name.toUpperCase()} ${result.severity}`;

            return <Badge key={`${result.name}-${result.severity}`} size={BadgeSize.Small} text={displayText} />;
          }) || []}
        </div>
      </div>
    </>
  );
}
